import React, {
  cloneElement, CSSProperties, forwardRef, Fragment,
  memo, ReactElement, ReactNode, useEffect, useState
} from "react";
import { createPortal } from "react-dom";
import * as PopperJS from "@popperjs/core";
import { Placement } from "@popperjs/core/lib/enums";
import classNames from "classnames";
import { Modifier, usePopper } from "react-popper";
import { animated, useTransition } from "react-spring";
import { mergeRefs, useRefEffect } from "../../../utils";

import style from "./style.module.scss";

type AnimatedDivProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties & {
    [key: string]: any;
  };
};

export type PopperInstance = {
  styles: { [key: string]: CSSProperties };
  attributes: { [key: string]: { [key: string]: string } | undefined };
  state: PopperJS.State | null;
  update: PopperJS.Instance["update"] | null;
  forceUpdate: PopperJS.Instance["forceUpdate"] | null;
}

export type TooltipProps = {
  content?: ReactNode,
  children: ReactElement<
  {
    ref?: React.Ref<any>;
    onPointerEnter?: React.PointerEventHandler<HTMLElement>;
    onPointerLeave?: React.PointerEventHandler<HTMLElement>;
  }
  >,
  placement?: Placement,
  arrow?: boolean,
  className?: string,
  delay?: number,
  virtualReferenceElement?: PopperJS.VirtualElement,
  externalIsOpen?: boolean,
  setExternalIsOpen?: (isOpen: boolean) => void,
  options?: Omit<Partial<PopperJS.Options>, "modifiers"> & {
    createPopper?: typeof PopperJS.createPopper;
    modifiers?: ReadonlyArray<Modifier<any>>;
  }
}

export const Tooltip = memo(forwardRef<PopperInstance, TooltipProps>((
  {
    content,
    placement = "bottom",
    arrow = true,
    className,
    delay,
    children,
    options,
    externalIsOpen,
    setExternalIsOpen,
    virtualReferenceElement,
  }, ref) => {

  if (children.type === Fragment) {
    throw new Error("Don't use fragments inside a | Tooltip | components");
  }

  const [ isOpen, setIsOpen ] = useState(false);
  const [ referenceElement, setReferenceElement ] = useState<HTMLDivElement | null>(null);
  const [ popperElement, setPopperElement ] = useState<HTMLDivElement | null>(null);
  const [ arrowElement, setArrowElement ] = useState<HTMLDivElement | null>(null);

  const instance = usePopper(virtualReferenceElement ?? referenceElement, popperElement, {
    placement: placement,
    ...options,
    modifiers: [
      { name: "preventOverflow", options: { padding: 10 } },
      { name: "arrow", options: { element: arrowElement, padding: 10 } },
      { name: "offset", options: { offset: [ 0, 8 ] } },
      ...(options?.modifiers || []),
    ],
  });

  const { styles, attributes, update } = instance;

  const externalControl = externalIsOpen !== undefined && setExternalIsOpen;

  useRefEffect(ref, instance);

  useEffect(() => {
    update?.();
  }, [ content, update ]);

  const transitions = useTransition([ isOpen, externalIsOpen ],
    {
      from: { opacity: 0 },
      enter: { opacity: 1, delay: (delay ?? 600) },
      leave: { opacity: 0 },
      config: {
        duration: 300,
      },
    },
  );

  if (!content) {
    return children;
  }
  const AnimatedDiv = animated("div") as React.ForwardRefExoticComponent<AnimatedDivProps & React.RefAttributes<HTMLDivElement>>;

  return (
    <>
      {cloneElement(children, {
        ref: mergeRefs([ children.props.ref, setReferenceElement ]),
        onPointerEnter: (e: any) => {
          children.props.onPointerEnter?.(e);
          if (!e.defaultPrevented) {
            externalControl ? setExternalIsOpen(true) : setIsOpen(true);
          }
        },
        onPointerLeave: (e: any) => {
          children.props.onPointerLeave?.(e);
          if (!e.defaultPrevented) {
            externalControl ? setExternalIsOpen(false) : setIsOpen(false);
          }
        },
      })}
      {(externalControl ? externalIsOpen : isOpen) &&
        createPortal(
          transitions((props, item) => item && (
            <AnimatedDiv
              className={classNames(style.tooltip, className)}
              ref={setPopperElement}
              style={{ ...(styles.popper as any), ...props }} {...attributes.popper}
            >
              {content}
              {arrow && <div className={style.arrow} style={styles.arrow} ref={setArrowElement} />}
            </AnimatedDiv>
          )),
          document.querySelector("#popperPortal")!,
        )
      }
    </>);
}));


export default Tooltip;


